import { FunctionComponent, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { faPencil, faPlus, faTrash } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { TextArea } from "@/components/TextArea/TextArea";
import { classNames } from "@/utils/classNames";

import { isTempId } from "../../shell/configDraft";
import { DetailHeader } from "../../shell/DetailHeader";
import { PersonaSegmentTable } from "../../shell/PersonaSegmentTable";
import { MatrixCellActions } from "../../shell/ScoreMatrix/MatrixCell";
import { scoringOptions } from "../../shell/ScoreMatrix/matrixModel";
import { SegmentView } from "../../shell/scoreConfigView";
import { ConfigDraftApi } from "../../shell/useConfigDraft";
import { useFlashOnFocus } from "../../shell/useFocusTarget";
import { ScoreCriteriaType } from "../../schema/score.schema";

interface SegmentDetailProps extends MatrixCellActions {
	segment: SegmentView;
	edits: ConfigDraftApi;
	criteria: ScoreCriteriaType[];
	flash: boolean;
	onFlashDone: () => void;
	onDelete: (segment: SegmentView) => void;
	onCreatePersona: () => void;
}

const ACTION =
	"flex h-6 cursor-pointer items-center gap-1 rounded-md border px-2 text-[11px] font-medium";

/**
 * The card under the chip row for the selected segment (PREC-4055): activate
 * toggle, editable name, description, and which personas exist here with
 * which lead scoring. Investor types have no description of their own.
 */
export const SegmentDetail: FunctionComponent<SegmentDetailProps> = ({
	segment,
	edits,
	criteria,
	flash,
	onFlashDone,
	onDelete,
	onCreatePersona,
	...cellActions
}) => {
	const { t } = useTranslation();
	const cardRef = useRef<HTMLDivElement>(null);
	const [renameSignal, setRenameSignal] = useState(0);
	const isInvestor = segment.investorName !== null;
	const unsaved = segment.id !== null && isTempId(segment.id);

	useFlashOnFocus(cardRef, flash, onFlashDone);

	const rows = useMemo(
		() =>
			edits.personas
				.filter((persona) => persona.active)
				.map((persona) => ({
					segment,
					persona,
					linked: edits.isLinked(segment, persona),
					assignment: edits.assignmentOf(segment, persona),
				})),
		[edits, segment]
	);
	const options = useMemo(() => scoringOptions(criteria), [criteria]);

	const setActive = (active: boolean) =>
		isInvestor
			? edits.setInvestorSelected(segment.investorName as string, active)
			: edits.updateSegment(segment.id as number, { active });

	return (
		<div
			ref={cardRef}
			className={classNames(
				"mt-3 overflow-hidden rounded-lg border border-border-light bg-white",
				!segment.active && "opacity-90"
			)}
			data-testid={`segment-detail-${segment.key}`}
		>
			<DetailHeader
				active={segment.active}
				activeTitle={t("leadScoring.segments.segmentActive")}
				inactiveTitle={t("leadScoring.segments.segmentInactive")}
				name={segment.name}
				renameSignal={renameSignal}
				testId="segment-detail"
				actions={
					!isInvestor && (
						<>
							<button
								className={classNames(
									ACTION,
									"border-transparent text-text-meta hover:bg-background-light"
								)}
								type="button"
								onClick={() => setRenameSignal((n) => n + 1)}
							>
								<Icon className="h-3 w-3" icon={faPencil} />
								{t("leadScoring.detail.rename")}
							</button>
							<button
								className={classNames(
									ACTION,
									"border-status-error-light text-status-error hover:bg-error-light"
								)}
								data-testid="segment-delete"
								type="button"
								onClick={() => onDelete(segment)}
							>
								<Icon className="h-3 w-3" icon={faTrash} />
								{t("leadScoring.detail.deletePermanently")}
							</button>
						</>
					)
				}
				onRename={isInvestor ? undefined : (name) => edits.rename("segment", segment, name)}
				onToggle={setActive}
			/>
			<div className={classNames("px-3.5 py-3.5", !segment.active && "opacity-60")}>
				{!isInvestor && (
					<TextArea
						className="min-h-[80px]"
						handleChange={(event) =>
							edits.updateSegment(segment.id as number, { description: event.target.value })
						}
						name={`segment-description-${segment.key}`}
						placeholder={t("leadScoring.segments.descriptionPlaceholder")}
						showError={false}
						value={segment.description}
					/>
				)}
				<div className="mb-2 mt-4 text-[10px] font-bold uppercase tracking-wider text-text-disabled">
					{t("leadScoring.segments.personasInSegment")}
				</div>
				{unsaved ? (
					<p className="text-xs text-text-meta" data-testid="segment-save-first">
						{t("leadScoring.segments.saveFirst")}
					</p>
				) : (
					<PersonaSegmentTable
						axis="persona"
						options={options}
						rows={rows}
						onToggleLink={edits.setLinked}
						{...cellActions}
					/>
				)}
				<button
					className={classNames(
						ACTION,
						"mt-2.5 border-border-light bg-white text-text hover:bg-background-light"
					)}
					type="button"
					onClick={onCreatePersona}
				>
					<Icon className="h-3 w-3" icon={faPlus} />
					{t("leadScoring.segments.createPersona")}
				</button>
			</div>
		</div>
	);
};
